import React, { useEffect, useMemo } from 'react';
import c from 'classnames';
import type { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

import { useExtendedFormikContext } from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/FormikWrapper';

import {
  WEBFORM_KEY_CHECK_OUT_DB_APP,
  WEBFORM_KEY_CHECK_OUT_DB_HEARING,
  WEBFORM_KEY_CHECK_OUT_DB_METER,
  WEBFORM_KEY_CHECK_OUT_GOING_OUT_DURATION,
  WEBFORM_KEY_CHECK_OUT_GOING_OUT_FREQUENCY,
  WEBFORM_KEY_CHECK_OUT_HIDDEN_FIELD,
} from '../../../../config/constants';

import './style.scss';

function log10(value: number) {
  return Math.log(value) / Math.LN10;
}

type CheckOutResult = 'success' | 'warning' | 'error';

function calculateResult(
  goingOutFrequency: number,
  goingOutDuration: number,
  dbMeter?: number,
  dbApp?: number,
  dbHearing?: number,
): CheckOutResult {
  const db = dbMeter || dbApp || dbHearing;

  if (!db) {
    return 'error';
  }

  const soundDose = db - 10 * log10(8 / goingOutDuration) - 10 * log10(5 / goingOutFrequency);

  if (soundDose <= 88) {
    return 'success';
  }

  if (soundDose > 88 && soundDose <= 92.5) {
    return 'warning';
  }

  return 'error';
}

interface ExtendedFormikContext extends FormikProps<{ [key: string]: string }> {}

const WebformCustomCheckOut: React.FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'components.webform.custom.check_out' });
  const { values, setFieldValue } = useExtendedFormikContext() as ExtendedFormikContext;

  const result = useMemo(() => {
    const goingOutFrequency = values[WEBFORM_KEY_CHECK_OUT_GOING_OUT_FREQUENCY];
    const goingOutDuration = values[WEBFORM_KEY_CHECK_OUT_GOING_OUT_DURATION];
    const dbMeter = values[WEBFORM_KEY_CHECK_OUT_DB_METER];
    const dbApp = values[WEBFORM_KEY_CHECK_OUT_DB_APP];
    const dbHearing = values[WEBFORM_KEY_CHECK_OUT_DB_HEARING];

    if (!(goingOutFrequency && goingOutDuration && (dbMeter || dbApp || dbHearing))) {
      return undefined;
    }

    return calculateResult(
      Number(goingOutFrequency),
      Number(goingOutDuration),
      Number(dbMeter),
      Number(dbApp),
      Number(dbHearing),
    );
  }, [values]);

  useEffect(() => {
    if (result) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      setFieldValue(WEBFORM_KEY_CHECK_OUT_HIDDEN_FIELD, t(`result_label_${result}`));
    }
  }, [result, setFieldValue, t]);

  const classes = c('WebformCustomCheckOut', {
    [`WebformCustomCheckOut--result--${String(result)}`]: result !== undefined,
    [`u-background--system--${String(result)}--400`]: result !== undefined,
  });

  if (!result) {
    return null;
  }

  return (
    <div className={classes}>
      <span className="WebformCustomCheckOut__result-label">
        {t(`result_label_${result}`)}
      </span>

      <p className="WebformCustomCheckOut__result-description">
        {t(`result_description_${result}`)}
      </p>
    </div>
  );
};

export default WebformCustomCheckOut;
